import {
	ChangeDetectionStrategy,
	Component,
	EventEmitter,
	Inject,
	OnDestroy,
	OnInit, Output
} from '@angular/core';
import { AsyncPipe, DatePipe, NgClass } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { GoMaterialModule } from '@go-modules/go-material.module';
import { FormatTimeModule } from '@go-modules/pipes/format-time/format-time.module';
import { NgxZeroStateModule } from '@go-modules/components/zero-state/zero-state.module';
import { MarkerDisplayModule } from '@go-modules/components/marker-display/marker-display.module';
import { MatIconModule } from '@angular/material/icon';
import { EnvironmentVarsService } from '@go-modules/services/environment-vars/environment-vars.service';
import { ENVIRONMENTS } from '@go-modules/services/environment-vars/environments';
import { User as UserFactory } from 'go-modules/models/user/user.factory';
import { Group as GroupFactory, groupToken } from 'go-modules/models/group-dep/group.factory';
import {
	BehaviorSubject, catchError, EMPTY, interval, Subject, switchMap, take, takeUntil, tap
} from 'rxjs';
import { GoModalService } from '@go-modules/services/go-modal/go-modal.service';
import { DropUserDialogComponent } from '../dialogs/drop-user-dialog/drop-user-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmDialogComponent } from '../dialogs/confirm-dialog/confirm-dialog.component';
import { MessageDialogComponent } from '../dialogs/message-dialog/message-dialog.component';
import { SelectedService, selectedServiceToken } from 'go-modules/services/selected/selected.service';
import { UserService, userServiceToken } from 'go-modules/models/user/user.service';
import { CreditBackResult } from '@go-modules/interfaces/groups/group.interface';
import { NgxGroupService } from '@go-modules/services/group/group.service';
import { NgxBraintreeService } from '@go-modules/services/braintree/braintree.service';
import { AppDataService } from '@go-modules/services/app-data/app-data.service';
import { ActiveBrandService } from '@go-modules/services/active-brand';

export enum PAYMENT_TRANSACTION_STATUS {
	LOADING = 'LOADING',
	PENDING = 'PENDING',
	DONE = 'COMPLETE'
}

@Component({
	selector: 'ngx-course-payment-wall',
	standalone: true,
	imports: [
		TranslateModule,
		GoMaterialModule,
		FormatTimeModule,
		NgxZeroStateModule,
		MarkerDisplayModule,
		MatIconModule,
		AsyncPipe,
		DatePipe,
		NgClass
	],
	templateUrl: './course-payment-wall.component.html',
	styleUrls: ['./course-payment-wall.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class CoursePaymentWallComponent implements OnInit, OnDestroy {
	public course: ReturnType<typeof GroupFactory>;
	public user: ReturnType<typeof UserFactory>;

	@Output() public onCredit = new EventEmitter();
	@Output() public onPay = new EventEmitter();

	public environmentVarsService: EnvironmentVarsService;
	public isLtiEnvironment = false;
	public paymentTransactionStatus = PAYMENT_TRANSACTION_STATUS;
	public paymentTransactionState$ = new BehaviorSubject(PAYMENT_TRANSACTION_STATUS.LOADING);
	public canBeCredited: CreditBackResult;
	private destroy$ = new Subject<void>();

	constructor (
		private ngxGoModalService: GoModalService,
		private dialog: MatDialog,
		private translate: TranslateService,
		private groupService: NgxGroupService,
		private braintreeService: NgxBraintreeService,
		private appDataService: AppDataService,
		public activeBrandService: ActiveBrandService,

		@Inject(selectedServiceToken) private selectedService: SelectedService,
		@Inject(userServiceToken) private userService: UserService,
		@Inject(groupToken) private Group: ReturnType<typeof GroupFactory>,
		@Inject('Window') private window: Window
	) {
		this.environmentVarsService = EnvironmentVarsService.getInstance();
	}

	public ngOnInit (): void {
		this.course = this.selectedService.getGroup();
		this.user = this.userService.currentUser;
		this.canBeCredited = { credit_back: false };

		const environment = this.environmentVarsService.get(EnvironmentVarsService.VAR.ENVIRONMENT) as any;
		this.isLtiEnvironment = environment.name === ENVIRONMENTS.LTI;

		// Determine if credit can be issued back
		this.groupService.canBeCredited(this.user.getId(), this.course.getId())
			.subscribe((result) => this.canBeCredited = result);

		// Check if there is pending payment transaction
		this.handlePendingPaymentTransaction();
	}

	public ngOnDestroy (): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	/**
	 * Opens the move user modal
	 */
	public openDropUserModal () {
		return this.ngxGoModalService.open(DropUserDialogComponent, false, { data: {
			group: this.course,
			user: this.user
		}})
			.afterClosed()
			.subscribe((result: { drop: boolean }) => {
				if (result.drop) {
					this.window.location.reload();
				} else if (result.drop === false) {
					MessageDialogComponent.open(this.dialog, {
						data: {
							title: this.translate.instant('drop-user-dialog_title'),
							message: this.translate.instant('modal-move-user_controller-message')
						}
					});
				}
			});
	}

	public openUnregisterModal () {
		let message = this.translate.instant('modal-move-user_controller-sure-drop-course');

		if (this.canBeCredited.credit_back) {
			message += ' ' + this.translate.instant('modal-move-user_controller-credit-received');
		} else {
			message += ' ' + this.translate.instant('modal-move-user_controller-no-credit-received');
		}

		return ConfirmDialogComponent.open(this.dialog, {
			data: {
				title: this.translate.instant('modal-move-user_controller-drop', {name: this.course.name}),
				message,
				confirmText: this.translate.instant('modal-move-user_controller-drop-course'),
				cancelText: this.translate.instant('common_cancel')
			}
		}).afterClosed().subscribe((confirm) => {
			if (confirm) {
				this.groupService.deleteUser(this.user.getId(), this.course.getId()).pipe(
					tap(() => this.reloadPage()),
					catchError(() => {
						MessageDialogComponent.open(this.dialog, {
							data: {
								title: this.translate.instant('modal-move-user_controller-uh-oh'),
								message: this.translate.instant('modal-move-user_controller-message')
							}
						});
						return EMPTY;
					})

				).subscribe();
			}
		});
	}

	private handlePendingPaymentTransaction () {
		this.braintreeService.getPaymentTransactionStatus(this.course.getId()).pipe(
			tap(() => this.paymentTransactionState$.next(PAYMENT_TRANSACTION_STATUS.PENDING)),
			switchMap(() =>
				interval(10000).pipe(
					switchMap(() => this.checkPendingPaymentTransaction())
				)
			),
			catchError(() => {
				this.paymentTransactionState$.next(PAYMENT_TRANSACTION_STATUS.DONE);
				return EMPTY;
			}),
			takeUntil(this.destroy$)
		).subscribe();
	}

	private checkPendingPaymentTransaction () {
		return this.braintreeService.getPaymentTransactionStatus(this.course.getId()).pipe(
			catchError((error) => {
				if (error.status === 404) {
					this.Group.get(this.course.getId(), (res) => {
						if(!res.hasPaid()) {
							const data = {
								title: this.translate.instant('payment-panel-payment-error_payment-processing-error'),
								message: this.translate.instant('payment-unsuccessful-transaction'),
								confirmText: this.translate.instant('common_refresh-page')
							};
							const dialogRef = ConfirmDialogComponent.open(this.dialog, { data });
							dialogRef.afterClosed().subscribe((confirm: boolean) => {
								if (confirm) {
									this.reloadPage();
								}
							});
						} else {
							this.reloadPage();
						}

					}, /*fail*/ null, /*ignoreCache*/ true);
					return EMPTY;
				}
				return EMPTY;
			}),
			takeUntil(this.destroy$)
		);
	}

	private reloadPage () {
		if (this.isLtiEnvironment) {
			this.appDataService.appData$.pipe(
				takeUntil(this.destroy$),
				take(1)
			).subscribe((data) => {
				const appData = data;
				appData.prompts.pay = false;
				appData.prompts.setupLicense = false;
				// remove signature so if frame is reloaded, we dont show banner
				delete appData.signature;
				this.window.sessionStorage.setItem('appData', JSON.stringify(appData));
				this.appDataService.setAppData(appData);

				this.window.history.replaceState({}, document.title, this.window.location.pathname);
				this.window.location.reload();
			});
		} else {
			this.window.location.reload();
		}
	}
}
